import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Check, ArrowRight } from 'lucide-react';
import { Button, Mascot, Modal, cn } from '@nexply/ui';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

interface Step {
  title: string;
  description: string;
  cta: string;
  to: string;
  /** Erledigt sich anhand echter Daten, nicht anhand geklickter Buttons. */
  done: boolean;
}

/**
 * Interaktiver Schnellstart beim ersten Login.
 *
 * Bewusst kein reiner Willkommens-Slider: die Schritte haken sich ab, sobald die Daten
 * tatsächlich vorhanden sind (Profil ausgefüllt, Kontakte angelegt, Match erzeugt). So
 * führt der Schnellstart genau durch die Kette, die den Produktnutzen ausmacht —
 * Netzwerk strukturieren → Kontakte erfassen → KI vernetzen.
 */
export function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  // Für diese Sitzung weggeklickt (z.B. weil der Nutzer gerade einen Schritt erledigt).
  // Bewusst getrennt von onboardedAt: wer einen Schritt anfängt, soll die Liste beim
  // nächsten Login wiedersehen — dann aber mit abgehaktem Schritt.
  const [dismissed, setDismissed] = useState(false);

  const open = !!user && user.onboardedAt === null && !dismissed;

  const profile = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: () => api.profile.me(),
    enabled: open,
  });
  const members = useQuery({
    queryKey: ['members', { onboarding: true }],
    queryFn: () => api.members.list({ limit: 1 }),
    enabled: open,
  });
  const matches = useQuery({
    queryKey: ['matches', { onboarding: true }],
    queryFn: () => api.matches.list(),
    enabled: open,
  });

  const finish = useMutation({
    mutationFn: () => api.account.markOnboarded(),
    onSuccess: () => refresh(),
  });

  if (!open) return null;

  const me = profile.data?.member ?? null;
  const steps: Step[] = [
    {
      title: 'Eigenes Profil ausfüllen',
      description:
        'Was du anbietest und was du suchst — das ist die Grundlage, auf der die KI dich mit den richtigen Leuten zusammenbringt.',
      cta: 'Zum Profil',
      to: '/profile',
      done: !!me && (!!me.offering || !!me.seeking || !!me.pitch),
    },
    {
      title: 'Kontakte anlegen oder importieren',
      description:
        'Trage dein Netzwerk ein oder importiere eine CSV-Datei. Mit Stadt und Branche erscheinen die Kontakte auch auf der Karte.',
      cta: 'Zu den Mitgliedern',
      to: '/members',
      done: (members.data?.total ?? 0) > 0,
    },
    {
      title: 'Erstes KI-Match erzeugen',
      description:
        'Lass die KI vorschlagen, wer in deinem Netzwerk zueinander passt — und warum.',
      cta: 'Zu den Matches',
      to: '/matches',
      done: (matches.data?.matches.length ?? 0) > 0,
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;

  // Einen Schritt anzugehen beendet den Schnellstart *nicht* — nur "Später"/"Los geht's" tun das.
  const goTo = (to: string) => {
    setDismissed(true);
    void navigate({ to });
  };

  return (
    <Modal
      open={open}
      // Außenklick/Escape = "nur wegklicken", nicht "nie wieder zeigen". Dauerhaft beendet
      // wird der Schnellstart ausschließlich über den Button unten.
      onClose={() => setDismissed(true)}
      title="Willkommen bei Nexply"
      description="In drei Schritten wird aus deiner Kontaktliste ein Netzwerk, das arbeitet."
      size="lg"
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-text-muted">
            {doneCount} von {steps.length} erledigt
          </span>
          <div className="flex items-center gap-2">
            {!allDone && (
              <button
                onClick={() => finish.mutate()}
                disabled={finish.isPending}
                className="text-xs text-text-muted transition hover:text-text"
              >
                Nicht mehr anzeigen
              </button>
            )}
            <Button
              variant={allDone ? 'primary' : 'ghost'}
              onClick={() => (allDone ? finish.mutate() : setDismissed(true))}
              disabled={finish.isPending}
            >
              {allDone ? 'Los geht’s' : 'Später'}
            </Button>
          </div>
        </div>
      }
    >
      {/* Erster Auftritt von Nex — hier lernt der Nutzer die Figur kennen, die ihm später
          bei jeder KI-Ausgabe wieder begegnet. */}
      <div className="mb-5 flex items-center gap-4 rounded-2xl border border-gold/25 bg-gold/[0.06] p-4">
        <Mascot mood={allDone ? 'happy' : 'idle'} size={56} />
        <p className="text-sm leading-relaxed text-text-dim">
          Ich bin <span className="font-medium text-gold">Nex</span>. Ich helfe dir, aus deinen
          Kontakten ein Netzwerk zu machen, das für dich arbeitet.
        </p>
      </div>

      <ol className="space-y-3">
        {steps.map((step, i) => (
          <li
            key={step.title}
            className={cn(
              'flex gap-3 rounded-2xl border p-4 transition',
              step.done
                ? 'border-success/30 bg-success/[0.06]'
                : 'border-line bg-raise',
            )}
          >
            <div
              className={cn(
                'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                step.done ? 'bg-success/20 text-success' : 'bg-gold/15 text-gold',
              )}
            >
              {step.done ? <Check className="h-4 w-4" /> : i + 1}
            </div>

            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-text">{step.title}</div>
              <p className="mt-1 text-xs leading-relaxed text-text-dim">{step.description}</p>

              {!step.done && (
                <button
                  onClick={() => goTo(step.to)}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-gold transition hover:text-gold-light"
                >
                  {step.cta}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </li>
        ))}
      </ol>
    </Modal>
  );
}
